import { Navigate, useLocation } from "react-router-dom";
import { MerchantRole, useAuth } from "@/hooks/useAuth";

/**
 * ProtectedRoute — gates a page behind four checks, in order:
 *
 *  1. signed in         (no session  → /auth)
 *  2. email verified    (unconfirmed → /verify-email, unless `requireVerified` is false)
 *  3. onboarded         (onboarded_at is null → /onboarding, unless `requireOnboarded` is false)
 *  4. role              (caller's role not in `requireRole` → /no-permission, unless unset)
 *
 * Role gating is an additional check on top of the first three. It is a UX
 * layer only — the source of truth for authorization is RLS in Postgres.
 * Hiding a page from a viewer and still having them call the API directly
 * must result in a 401/403 from the backend, not a client-side veto.
 */
export function ProtectedRoute({
  children,
  requireOnboarded = true,
  requireVerified = true,
  requireRole,
}: {
  children: JSX.Element;
  requireOnboarded?: boolean;
  requireVerified?: boolean;
  /** One or more roles that satisfy the gate. Undefined = no role check. */
  requireRole?: MerchantRole | MerchantRole[];
}) {
  const { session, profile, loading, emailVerified, currentRole, memberships } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="text-[13px] text-ink-3">Loading…</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requireVerified && !emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Wait for profile to load before deciding on onboarding gate
  if (requireOnboarded && profile && !profile.onboarded_at) {
    return <Navigate to="/onboarding" replace />;
  }

  if (requireRole) {
    // Still fetching memberships → show the same loading shell rather than
    // bouncing to /no-permission prematurely.
    if (memberships.length === 0 && !profile?.current_merchant_id) {
      return (
        <div className="flex h-screen items-center justify-center bg-canvas">
          <div className="text-[13px] text-ink-3">Loading…</div>
        </div>
      );
    }
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!currentRole || !allowed.includes(currentRole)) {
      return <Navigate to="/no-permission" state={{ from: location, required: allowed }} replace />;
    }
  }

  return children;
}
